import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

const PDFViewer = ({ pdfUrl }) => {
  const containerRef = useRef(null);
  const canvasRefs = useRef([]);
  const [numPages, setNumPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfDocument, setPdfDocument] = useState(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // Measure container width responsively
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width;
      if (w) setContainerWidth(w);
    });
    ro.observe(containerRef.current);
    setContainerWidth(containerRef.current.offsetWidth);
    return () => ro.disconnect();
  }, []);

  // Load PDF
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    pdfjsLib.getDocument({ url: pdfUrl, withCredentials: false, isEvalSupported: false })
      .promise
      .then(pdf => {
        if (!cancelled) {
          setPdfDocument(pdf);
          setNumPages(pdf.numPages);
          setLoading(false);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(`Failed to load PDF: ${err.message}`);
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [pdfUrl]);

  // Render pages whenever pdf or container width changes
  useEffect(() => {
    if (!pdfDocument || numPages === 0 || containerWidth === 0) return;

    const dpr = window.devicePixelRatio || 1;

    const renderPages = async () => {
      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        const canvas = canvasRefs.current[pageNum - 1];
        if (!canvas) continue;

        const page = await pdfDocument.getPage(pageNum);
        const naturalViewport = page.getViewport({ scale: 1 });

        // Scale so the page fills the container at native DPR sharpness
        const scale = (containerWidth / naturalViewport.width) * dpr;
        const viewport = page.getViewport({ scale });

        canvas.width = viewport.width;
        canvas.height = viewport.height;
        // CSS size = container width, height proportional
        canvas.style.width = containerWidth + 'px';
        canvas.style.height = Math.round(viewport.height / dpr) + 'px';

        await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
      }
    };

    renderPages();
  }, [pdfDocument, numPages, containerWidth]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3"></div>
          <p className="text-sm text-gray-500">Chargement de l'aperçu...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <svg className="h-10 w-10 text-red-400 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p className="text-sm text-red-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="select-none"
      onContextMenu={e => e.preventDefault()}
      style={{
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        backgroundColor: '#525659',
        maxHeight: '75vh',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {Array.from({ length: numPages }, (_, i) => (
        <div key={i} className="flex justify-center" style={{ padding: '12px 0', borderBottom: i < numPages - 1 ? '8px solid #525659' : 'none' }}>
          <canvas
            ref={el => (canvasRefs.current[i] = el)}
            style={{ display: 'block', userSelect: 'none', WebkitUserSelect: 'none', pointerEvents: 'none' }}
          />
        </div>
      ))}
    </div>
  );
};

export default PDFViewer;
